/**
 * Every word the four catalog forms and lists print — Board 1, Board 3 and
 * Board 18 (`docs/catalog-units-and-buying.md` §6–§7), written down once.
 *
 * The boards are literal (`build-exactly-as-drawn.md`), so a component never
 * spells one of these itself: it imports it from here, and a reviewer checks a
 * string against the board in one file. Words built from a unit take the unit's
 * own name (`The whole skid`, `Hire cost per week`); the sentences that carry
 * numbers are `shared/src/buying.ts`'s, beside the arithmetic they describe.
 */

/** The header's second line — what kind of record this is. */
export const KIND_SUB = {
  item: 'Material',
  labor: 'Labor',
  equipment: 'Equipment · hired in',
  fee: 'Fee',
} as const

export type CatalogKind = keyof typeof KIND_SUB

/* Section titles, in Board 1's fixed order. */
export const WHAT_IT_IS = 'What it is'
export const WHAT_IT_COSTS = 'What it costs, and how far one goes'
export const WHAT_IT_COSTS_SUB = 'buying unit, allocation unit, and the conversion between them'
export const WHAT_IT_SELLS_FOR = 'What it sells for'
export const WHERE_IT_CAN_BE_USED = 'Where it can be used'
export const EVERYWHERE_HINT = 'every branch, including any added later'
export const OPTIONS = 'Options'
export const TAXABLE = 'Taxable'
export const TRACK_STOCK = 'Track stock at the yards'
export const TRACK_STOCK_SUB = 'on-hand, reserved, purchasing'
export const NOTES = 'Notes'

/* What it is. */
export const NAME = 'Name'
export const DESCRIPTION = 'Description'
export const SKU = 'SKU or code'
export const SKU_HINT = 'optional · matches your supplier’s code'
export const CATEGORY = 'Category'
export const USUAL_VENDOR = 'Usual vendor'
export const HIRED_FROM = 'Hired from'

/* The two pairs. */
export const BUY_LABEL: Record<CatalogKind, string> = {
  item: 'Bought by', labor: 'Paid by', equipment: 'Hired by', fee: 'Paid by',
}
export const ALLOCATED_IN = 'Allocated in'

const lower = (word: string) => word.toLowerCase()
const capital = (word: string) => word.charAt(0).toUpperCase() + word.slice(1)

/** `Cost per skid` · `Loaded cost per person hour` · `Hire cost per week` · `Cost per haul`. */
export function costPerLabel(kind: CatalogKind, buyName: string): string {
  if (kind === 'labor') return `Loaded cost per ${lower(buyName)}`
  if (kind === 'equipment') return `Hire cost per ${lower(buyName)}`
  return `Cost per ${lower(buyName)}`
}

/** `Sq ft per skid` · `Days per week` · `Person hours per crew hour`. */
export function perLabel(allocMany: string, buyName: string): string {
  return `${capital(allocMany)} per ${lower(buyName)}`
}

export const PEOPLE = 'people'

/* The hint under the green sentence — a worked example per kind (§6.1). */
export const BAND_HINT: Record<CatalogKind, string> = {
  item: 'A skid of brick that covers 800 sq ft is “skid”, “sf”, 800.',
  labor: 'A two-person crew paid $30.25 loaded each is “person hour”, “crew hour”, 2.',
  equipment: 'A skid steer hired by the week and charged by the working day is “week”, “day”, 5.',
  fee: 'A 30 yd roll-off charged by the yard is “haul”, “cu yd”, 30.',
}

/* The minimum, under the sentence. */
export const MINIMUM_LABEL: Record<'item' | 'equipment' | 'fee', string> = {
  item: 'Minimum order', equipment: 'Minimum hire', fee: 'Minimum order',
}
export const MINIMUM_HINT: Record<'item' | 'equipment' | 'fee', string> = {
  item: 'what you usually have to buy · overridable on a quote',
  equipment: 'what the yard makes you take · overridable on a quote',
  fee: 'overridable on a quote',
}
export const LAST_PAID = 'Last paid'

/* The buying rule (§6.1): the label, the two segments and the hint, from the units. */
export function ruleLabel(kind: 'item' | 'equipment' | 'fee', buyName: string): string {
  const one = lower(buyName)
  if (kind === 'equipment') return `If a job keeps it part of a ${one}, it pays for`
  if (kind === 'fee') return `If a job fills part of a ${one}, it pays for`
  return `If a job uses part of a ${one}, it pays for`
}
export function ruleUsed(kind: 'item' | 'equipment' | 'fee', allocMany: string): string {
  return kind === 'equipment' ? `Only the ${lower(allocMany)} it uses` : 'Only what it uses'
}
export const ruleWhole = (buyName: string) => `The whole ${lower(buyName)}`
export function ruleHint(kind: 'item' | 'equipment' | 'fee', buyName: string): string {
  const one = lower(buyName)
  if (kind === 'equipment') return `The ${one} is paid for whether the job needs it or not.`
  if (kind === 'fee') return `A ${one} is paid in full however full it is.`
  return 'The leftover goes back to stock.'
}

/* Try it — internal, tinted, with the lock (§6.1). */
export const TRY_IT = 'Try it'
export const BUYS = 'buys'
export const COSTS_THE_JOB = 'Costs the job'
export const SELLS = 'Sells'
export const MARGIN = 'Margin'

/* Board 2's two-way compare under Try it — the rule's two answers side by side. */
export const ONE_SETTING = 'One setting'
export const costedTwoWays = (qty: string) => `the same ${qty}, costed two ways`
export const COSTS_THE_JOB_LOWER = 'costs the job'
export const MARGIN_LOWER = 'margin'

/* What it sells for. */
export const PRICED_BY = 'Priced by'
export const companyMarkup = (pct: number) => `Company markup · ${Math.round(pct)}%`
export const ITS_OWN_MARKUP = 'Its own markup'
export const A_FIXED_PRICE = 'A fixed price'
export const MARKUP_ON_COST = 'Markup on cost'
export const pricePer = (allocOne: string) => `Price per ${allocOne}`
export const costSlash = (allocOne: string) => `Cost / ${allocOne}`
export const sellsSlash = (allocOne: string) => `Sells / ${allocOne}`

/* The footer. */
export const SAVE = 'Save'
export const CANCEL = 'Cancel'
export const SAVE_AND_ADD_ANOTHER = 'Save & add another'

/* Board 3's buttons. */
export const NEW_BUTTON: Record<CatalogKind, string> = {
  item: 'New material', labor: 'New labor rate', equipment: 'New hired-in item', fee: 'New fee',
}

/** An owned machine's conversion cell, linking to where it is priced. */
/*
 * An owned kind of machine (0129): one row on the Equipment tab for every
 * machine of the type, opened in the hired-in form with these words where the
 * hire shop's were — same options, because it is the same row.
 */
export const OWNED_SUB = 'Equipment · owned'
export const KIND_OF_MACHINE = 'Kind of machine'
export const ownedMachinesHint = (count: number): string => (count === 0
  ? 'none in your fleet right now · not offered on a quote until there is one'
  : `${count === 1 ? '1 machine' : `${count} machines`} in your fleet · operations sends whichever is free`)
export const OWNED_BUY_LABEL = 'Costed by'
export const ownedCostPerLabel = (buyName: string): string => `Cost to us per ${buyName.toLowerCase()}`
export const OWNED_BAND_HINT = 'A mower costed by the day and charged by the hour is “day”, “hour”, 8.'
export const OWNED_MINIMUM_HINT = 'the least a job is charged for · overridable on a quote'
export const OWNED = 'Owned'
export const HIRED_IN = 'Hired in'
export const STOCKED = 'Stocked'

/* Board 2's stock card. */
export const STOCK_TITLE = (name: string) => `Stock · ${name}`
export const STOCK_RECEIVED = 'Received'
/** Board 2 draws one job's flow; on the item it is every job's (step-12.md). */
export const STOCK_USED = 'Used by jobs'
export const STOCK_LEFT = 'Left on hand'

/*
 * **Customer-facing name** (Aaron, step 24; migration 0126), directly under
 * Name on every catalog form a quote line can name. Its placeholder is the
 * record's Name, so an empty box visibly reads as "uses the name"; the quote
 * builder's line popovers wear the same label (`builder/copy.ts`).
 */
export const CUSTOMER_NAME = 'Customer-facing name'
export const CUSTOMER_NAME_HINT = 'Blank uses the name'
